import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { User as UserIcon, Plus, Search, ChevronLeft, ChevronRight, RefreshCw, Shield, AlertCircle } from 'lucide-react';
import { User, UserConnection, UserRole, UserStatus } from '../../types/admin';
import { LIST_USERS, CREATE_USER } from '../../graphql/admin';
import { useAuth } from '../../contexts/AuthContext';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import CreateUserModal from '../../components/admin/CreateUserModal';

/**
 * UserListPage - Display users in current tenant with search, filter, and pagination
 * Requirements: 11.1, 12.3
 */
const UserListPage: React.FC = () => {
  const { currentUser, loading: authLoading, error: authError } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const PAGE_SIZE = 10;

  // Handle authorization errors - redirect to unauthorized page
  useEffect(() => {
    if (!authLoading && authError) {
      // Check if it's an authorization error
      if (authError.toLowerCase().includes('unauthorized') || 
          authError.toLowerCase().includes('forbidden') ||
          authError.toLowerCase().includes('not found')) {
        navigate('/unauthorized', { replace: true });
      }
    }
  }, [authLoading, authError, navigate]);

  const fetchUsers = useCallback(async (token?: string | null) => {
    // Platform Admins have null tenantId but can still access this page
    // They would need to select a tenant first in a real implementation
    // For now, show a message if no tenantId is available
    if (!currentUser?.tenantId && currentUser?.role !== UserRole.PLATFORM_ADMIN) {
      setError('No tenant associated with your account');
      setLoading(false);
      return;
    }
    
    // Platform Admins without a selected tenant see an informational message
    if (!currentUser?.tenantId && currentUser?.role === UserRole.PLATFORM_ADMIN) {
      setError('Platform Admins: Please select a tenant from the Tenant Management page to view users');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock data for preview mode
        setUsers([
          { userId: '1', tenantId: 'tenant-1', role: UserRole.ADMIN, cognitoSub: 'sub-1', email: 'admin@example.com', name: 'John Admin', status: UserStatus.ACTIVE, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { userId: '2', tenantId: 'tenant-1', role: UserRole.USER, cognitoSub: 'sub-2', email: 'user1@example.com', name: 'Jane User', status: UserStatus.ACTIVE, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { userId: '3', tenantId: 'tenant-1', role: UserRole.USER, cognitoSub: null, email: 'pending@example.com', name: 'Pending User', status: UserStatus.PENDING_SYNC, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { userId: '4', tenantId: 'tenant-1', role: UserRole.USER, cognitoSub: 'sub-4', email: 'inactive@example.com', name: 'Inactive User', status: UserStatus.INACTIVE, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        ]);
        setNextToken(null);
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: LIST_USERS,
        variables: { tenantId: currentUser.tenantId, limit: PAGE_SIZE, nextToken: token }
      }) as any;

      const data: UserConnection = response.data?.listUsers;
      if (data) {
        setUsers(data.items);
        setNextToken(data.nextToken);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.tenantId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleNextPage = () => {
    if (nextToken) {
      setPrevTokens([...prevTokens, '']);
      fetchUsers(nextToken);
    }
  };

  const handlePrevPage = () => {
    if (prevTokens.length > 0) {
      const newPrevTokens = [...prevTokens];
      const prevToken = newPrevTokens.pop();
      setPrevTokens(newPrevTokens);
      fetchUsers(prevToken || null);
    }
  };

  const handleCreateUser = async (email: string, name: string, role: UserRole) => {
    const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                         !import.meta.env.VITE_COGNITO_USER_POOL_ID;

    if (isPreviewMode) {
      const newUser: User = {
        userId: Date.now().toString(),
        tenantId: currentUser?.tenantId || 'tenant-1',
        role,
        cognitoSub: null,
        email,
        name,
        status: UserStatus.PENDING_SYNC,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setUsers([newUser, ...users]);
      return;
    }

    const client = generateClient();
    await client.graphql({
      query: CREATE_USER,
      variables: { input: { email, name, role } }
    });
    
    fetchUsers();
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'Platform Admin';
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.USER:
        return 'User';
      default:
        return role;
    }
  };

  const getRoleBadgeStyles = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'bg-purple-100 text-purple-800';
      case UserRole.ADMIN:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter users by search query, status, and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage users in your tenant
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value={UserStatus.ACTIVE}>Active</option>
          <option value={UserStatus.INACTIVE}>Inactive</option>
          <option value={UserStatus.PENDING_SYNC}>Pending Sync</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Roles</option>
          <option value={UserRole.ADMIN}>Admin</option>
          <option value={UserRole.USER}>User</option>
        </select>
        <button
          onClick={() => fetchUsers()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* User table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      <UserIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                      <p>No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(user.role)}`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <UserStatusBadge status={user.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/admin/users/${user.userId}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={prevTokens.length === 0}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={!nextToken}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
      />
    </div>
  );
};

export default UserListPage;
